<template>
  <div v-if="show" class="custom-inline-panel" 
  :style="{
     top: `${top + currentScrollTop}px`,
     left: `${left + currentScrollLeft}px`,
     position: 'absolute'
   }">
    <div class="max-h-[264px] overflow-auto flex flex-col rounded-md shadow-md p-[0.8rem] bg-white w-[282px] mx-auto">
      
      <!-- Header Section -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <h3 class="text-[17px] font-semibold">Edit State</h3>
          
          <a
            href="#"
            @click.prevent="navigateToAddNewState"
            class="text-blue-500 text-sm font-medium hover:underline add-new-state-link mb-[5px]"
          >
            (+ Add New State)
          </a>
          <fluent-icon
            v-tooltip.bottom="'If you want to add new state, click on (+ Add New State) link.'"
            icon="info"
            size="16"
            class="info-color has-tooltip"
          />
        </div>
      </div>

      <!-- Search Input -->
      <div class="mt-[0.4rem]">
        <input
          v-model="search"
          type="text"
          class="search-input w-full border border-gray-300 rounded-md px-1.5 py-0.5 text-sm mb-2"
          placeholder="Search state..."
        />
      </div>

      <!-- State List -->
      <div class="mt-2 max-h-40 overflow-y-auto">
        <LabelDropdownItem
          v-for="state in filteredStates"
          :key="state.id"
          :title="state.name"
          :color="state.color"
          :selected="stateId === state.id"
          @click="selectState(state.id)"
        />
        <div
          v-if="noResult"
          class="text-sm text-gray-600 dark:text-white px-2 py-4 text-center"
        >
          No results found
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-2 mt-[0.6rem] justify-end">
        <span 
          v-if="selectedStateName"
          class="ml-2 px-2 py-[0.15rem] text-sm inline-flex items-center max-w-[200px] overflow-hidden"
        >
          <span class="text-gray-800 font-medium truncate max-w-[150px] overflow-hidden whitespace-nowrap">
            {{ selectedStateName }}
          </span>
          <!-- <button @click="clearSelectedState" class=" cancel-cross ml-1 mb-1 text-gray-600 hover:text-black text-xl leading-none cursor-pointer px-[0.4rem]">&times;</button> -->
          <woot-button
    icon="delete"
    variant="smooth"
    size="small"
    @click="clearSelectedState"
    class="action-button black-icon"
  />
        </span>

        <button 
          class="cancel-btn px-4 py-2 rounded border cursor-pointer" 
          @click.prevent="onCancel"
        >
          {{ $t('CONTACT_FORM.FORM.CANCEL') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Editor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import alertMixin from 'shared/mixins/alertMixin';
import { mapGetters } from 'vuex';
import LabelDropdownItem from 'shared/components/ui/label/LabelDropdownItem.vue';
import { picoSearch } from '@scmmishra/pico-search';

export default {
  components: {
    Editor,
    LabelDropdownItem,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    state_id: {
      type: Number,
      default: null,
    },
    contactId: {
      type: Number,
      default: null,
    },
    top: Number,
    left: Number,
    currentScrollLeft: { type: Number, default: 0 },
    currentScrollTop: { type: Number, default: 0 }
  },
  mixins: [alertMixin],
  watch: {
    state_id(newVal) {
      this.stateId = newVal;
      if(newVal!=null){
        this.getStateData(newVal);
      }
      else{
        this.stateId=null;
        this.remark=''
      }
    },
    contactId: {
    handler(newVal) {
      this.stateId = this.state_id; 
      if (this.stateId != null) {
        this.getStateData(this.stateId);
      }
    },
    immediate: true
  },

    show(newVal) {
      if (newVal) {
        this.isModalOpening = true;
        this.$nextTick(() => {
          setTimeout(() => {
            this.isModalOpening = false;
            document.addEventListener('click', this.handleOutsideClick);
          }, 100);
        });
      } else {
        document.removeEventListener('click', this.handleOutsideClick);
      }
    },
  },
  data() {
    return {
      remark: '',
      stateId: null,
      progress: false,
      originalStatusId: null,  
      originalRemark: '',
      search: '',
    };
  },
  mounted() {
    this.setInitialValues();
    this.$store.dispatch('pipelines/getStatus');
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId', // Getter to fetch accountId from Vuex
    }),
    states() {
      return this.$store.getters['pipelines/getAllStatus'];
    },
    filteredStates() {
      if (!this.search) {
        return this.states.filter(state => state.id !== this.stateId);
      }
      const results = picoSearch(this.states, this.search, ['name'], { threshold: 0.9 });
      return results.filter(state => state.id !== this.stateId);
    },
    noResult() {
      return this.filteredStates.length === 0 && this.search.trim() !== '';
    },
    selectedStateName() {
      const state = this.states.find(s => s.id === this.stateId);
      return state ? state.name : '';
    },
    formHasChanged() {
      return (
        this.stateId !== this.originalStatusId || 
        this.remark !== this.originalRemark
      );
    },
  },
  methods: {
    navigateToAddNewState() {
      // Get the base URL dynamically from the current window location
      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      
      // Ensure accountId is valid
      if (!this.accountId) {
        console.error("Account ID is undefined");
        return;
      }
      
      // Construct the URL dynamically using accountId
      const url = `${baseUrl}/app/accounts/${this.accountId}/settings/state?showAddState=true`;

      // Open the dynamically generated URL in a new tab
      this.onCancel();
      window.open(url, "_blank");
    },
    async selectState(stateId) {
      this.stateId = stateId;
      // Auto-submit when state is selected
      await this.createState();
    },
    handleOutsideClick(event) {
      if (this.isModalOpening) return;
      
      const panel = this.$el;
      if (panel && !panel.contains(event.target)) {
        this.onCancel();
      }
    },
    async removeState() {
      this.progress = true;
      const data = {
        stateId: null,
        contactId: this.contactId,
        description: '',
      };
      console.log('Removing state data:', data);
      const response = await this.$store.dispatch('contacts/assignState', data);
      bus.$emit('on-assign');
      if (response.status === 200) {
        this.showAlert('State Removed Successfully', { success: true });
      }
      this.onCancel();
      this.progress = false;
    },

    async clearSelectedState() {
      this.stateId = null;
      this.remark = '';
      this.$emit('update:state_id', null);
      await this.removeState();
    },
    onCancel() {
      this.search = ''
      document.removeEventListener('click', this.handleOutsideClick);
      this.$emit('cancel');
    },
    async getStateData(state_id) {
      const data={
        id: state_id,
        contact_id: this.contactId
      }
      const response = await this.$store.dispatch(
        'contacts/getState',data
      );
      console.log('Response', response);
      if (response.data.state_description !== null) {
        this.remark = response.data.state_description;
      }
      else{
        this.remark='';
      }
      this.setInitialValues();
    },
    setInitialValues() {
      this.originalStatusId = this.stateId;
      this.originalRemark = this.remark;
    },
    async createState() {
      this.progress=true;
      const data = {
        stateId: this.stateId,
        contactId: this.contactId,
        description: this.remark,
      };
      
      const response = await this.$store.dispatch('contacts/assignState', data);
      bus.$emit('on-assign');
      if (response.status == 200) {
        this.showAlert('State Update Successfully', { success: true });
      }
      this.progress=false;
      this.onCancel();
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-inline-panel {
  z-index: 1; // Ensure modal appears above table content
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2); // Make modal stand out
}

.justify-item {
  justify-content: space-around;
}
.button-color {
  background-color: #40518a;
}
.color {
  color: #40518a;
}
.editor-ui {
  border: 2px solid lightgrey;
  height: 200px;
  padding-left: 10px;
  margin: 0px 7px;
}
.cancel-btn{
  background-color: #fff;
  color: #40518a;
}
.cancel-btn:hover{
  background-color: #F9F9F9;
  color: #40518a;
}
/* Padding for Header */
.px-8 {
  padding-left: 1rem;
}

/* Styling for Add New State Link */
.add-new-state-link {
  //position: absolute;
  //top: 45px;
  //left: 134px;
  color: #40518a;
  font-size: 0.875rem; /* text-sm */
  font-weight: 500; /* font-medium */
  text-decoration: none;
}

.add-new-state-link:hover {
  text-decoration: underline;
  color: #0056b3;
}

/* Styling for Info Icon */
.info-icon-2 {
  position: absolute;
  top: 45px;
  right: 323px;
  color: black;
}

.info-icon:hover {
  color: #40518a;
}
.info-color{
  color:black;
}
.cancel-cross:hover{
  background-color: #F9F9F9;
}
.action-button {
  background: none !important;
  width: auto !important;
  color: #413f3f;
  margin-left:5px;
}
.dark .action-button{
  color: #3C4858;
}
.dark .text-gray-800 {
  color: #3C4858;
}

</style>